import React, { useState, createContext, useContext, useEffect, useCallback } from "react";
import { useApiService } from "../shared/apiservice";
import { StockRelocationRequest } from "../shared/types";

function parseRelocationRequest(d: any): StockRelocationRequest {
    return {
        id: d.id,
        product_stock: {
            ...d.product_stock,
            expires_at: d.product_stock?.expires_at ? new Date(d.product_stock.expires_at) : undefined,
        },
        location: d.location,
        level: d.level,
        column: d.column,
        split_pallet: d.split_pallet,
        user: d.user,
        status: d.status,
        created_at: new Date(d.created_at),
    };
}

// --- Submit hook (standalone, no context needed) ---

export function useStockRelocation() {
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const apiService = useApiService();

    async function requestIntoStock(product_stock_id: number): Promise<boolean> {
        setSubmitting(true);
        setError(null);

        try {
            const response = await apiService.post("/relocation/store", {
                product_stock_id,
                move_type: "stock",
            });

            if (!response.ok) {
                setError("Failed to submit request. Please try again.");
                return false;
            }

            return true;
        } catch (err) {
            console.error(err);
            setError("Failed to submit request. Please try again.");
            return false;
        } finally {
            setSubmitting(false);
        }
    }

    async function requestRelocation(
        product_stock_id: number,
        stock_location_id: number,
        level: number,
        column: number | undefined,
        split_pallet: boolean
    ): Promise<boolean> {
        setSubmitting(true);
        setError(null);

        try {
            const payload: Record<string, any> = {
                move_type: 'relocate',
                product_stock_id,
                stock_location_id,
                level,
                split_pallet
            };
            if (column != null) payload.column = column;

            const response = await apiService.post("/relocation/store", payload);

            if (!response.ok) {
                setError("Failed to submit relocation request. Please try again.");
                return false;
            }

            return true;
        } catch (err) {
            console.error(err);
            setError("Failed to submit relocation request. Please try again.");
            return false;
        } finally {
            setSubmitting(false);
        }
    }

    async function cancelRelocation(relocationId: number): Promise<boolean> {
        setSubmitting(true);
        setError(null);

        try {
            const response = await apiService.delete(
                `/relocation/${relocationId}/delete`
            );

            if (!response.ok) {
                setError("Failed to cancel relocation request. Please try again.");
                return false;
            }

            return true;
        } catch (err) {
            console.error(err);
            setError("Failed to cancel relocation request. Please try again.");
            return false;
        } finally {
            setSubmitting(false);
        }
    }

    return { requestIntoStock, requestRelocation, cancelRelocation, submitting, error };
}

// --- Detail hook ---

export function useStockRelocationDetail(id?: string) {
    const [relocation, setRelocation] = useState<StockRelocationRequest | null>(null);
    const [errors, setErrors] = useState<string[]>([]);
    const [loading, setLoading] = useState(false);
    const apiService = useApiService();

    const fetchRelocation = useCallback(async () => {
        if (!id) return;
        setLoading(true);
        setErrors([]);

        try {
            const response = await apiService.get(`/relocation/${id}`);
            if (!response.ok) {
                setErrors(["Failed to load relocation request."]);
                return;
            }
            const json = await response.json();
            setRelocation(parseRelocationRequest(json.data ?? json));
        } catch (err) {
            console.error(err);
            setErrors(["Failed to load relocation request."]);
        } finally {
            setLoading(false);
        }
    }, [id]);

    useEffect(() => {
        fetchRelocation();
    }, [fetchRelocation]);

    return { relocation, errors, loading, fetchRelocation };
}

// --- List provider ---

export type SortOrder = "asc" | "desc";

const stockRelocationContext = createContext<{
    relocationRequests: StockRelocationRequest[];
    errors: string[];
    loading: boolean;
    page: number;
    perPage: number;
    total: number;
    searchTerm: string;
    sortField: string;
    sortOrder: SortOrder;
    fetchRelocationRequests: (silent?: boolean) => void;
    setPage: (page: number) => void;
    setPerPage: (perPage: number) => void;
    setSearchTerm: (term: string) => void;
    setSort: (field: string) => void;
}>({
    relocationRequests: [],
    errors: [],
    loading: false,
    page: 1,
    perPage: 15,
    total: 0,
    searchTerm: "",
    sortField: "created_at",
    sortOrder: "desc",
    fetchRelocationRequests: () => { },
    setPage: () => { },
    setPerPage: () => { },
    setSearchTerm: () => { },
    setSort: () => { },
});

function useProviderStockRelocationRequests() {
    const [relocationRequests, setRelocationRequests] = useState<StockRelocationRequest[]>([]);
    const [errors, setErrors] = useState<string[]>([]);
    const [loading, setLoading] = useState(false);
    const [page, setPageInternal] = useState(1);
    const [perPage, setPerPage] = useState(15);
    const [total, setTotal] = useState(0);
    const [searchTerm, setSearchTermInternal] = useState("");
    const [sortField, setSortField] = useState("created_at");
    const [sortOrder, setSortOrder] = useState<SortOrder>("desc");
    const apiService = useApiService();

    function setSearchTerm(term: string) {
        setSearchTermInternal(term);
        setPageInternal(1);
    }

    function setPage(p: number) {
        setPageInternal(p);
    }

    function setSort(field: string) {
        if (field === sortField) {
            setSortOrder(sortOrder === "asc" ? "desc" : "asc");
        } else {
            setSortField(field);
            setSortOrder("asc");
        }
        setPageInternal(1);
    }

    useEffect(() => {
        fetchRelocationRequests();
    }, [page, perPage, searchTerm, sortField, sortOrder]);

    async function fetchRelocationRequests(silent?: boolean) {
        if (!silent) setLoading(true);
        setErrors([]);

        try {
            const params = new URLSearchParams({
                page: String(page),
                per_page: String(perPage),
                sort: sortField,
                order: sortOrder,
            });
            if (searchTerm.trim()) {
                params.set("q", searchTerm.trim());
            }
            const response = await apiService.get(`/relocation?${params.toString()}`);
            if (!response.ok) {
                setErrors(["Loading relocation requests failed. Please try again."]);
                return;
            }
            const json = await response.json();
            const data: StockRelocationRequest[] = (json.data ?? json).map(parseRelocationRequest);
            setTotal(json.meta?.total ?? data.length);
            setRelocationRequests(data);
        } catch (error) {
            console.error(error);
            setErrors(["Loading relocation requests failed. Please try again."]);
        } finally {
            setLoading(false);
        }
    }

    return {
        relocationRequests,
        errors,
        loading,
        page,
        perPage,
        total,
        searchTerm,
        sortField,
        sortOrder,
        fetchRelocationRequests,
        setPage,
        setPerPage,
        setSearchTerm,
        setSort,
    };
}

export function ProviderStockRelocationRequests({ children }: { children: React.ReactNode }) {
    const value = useProviderStockRelocationRequests();
    return (
        <stockRelocationContext.Provider value={value}>
            {children}
        </stockRelocationContext.Provider>
    );
}

export const useStockRelocationRequests = () => {
    return useContext(stockRelocationContext);
};
